import { supabase } from '../lib/supabase';

export const advancedBillingService = {
  // ============================================
  // DISCOUNT RULES MANAGEMENT
  // ============================================

  // Get all discount rules with optional filters
  getAllDiscountRules: async (filters = {}) => {
    try {
      // Fetch discount rules without join (country_ids is an array, not a foreign key)
      let query = supabase?.from('discount_rules')?.select('*');
      
      if (filters?.discountType && filters?.discountType !== 'all') {
        query = query?.eq('discount_type', filters?.discountType);
      }
      if (filters?.isActive !== undefined) {
        query = query?.eq('is_active', filters?.isActive);
      }
      if (filters?.countryId) {
        query = query?.contains('country_ids', [filters?.countryId]);
      }
      
      const { data: rules, error: rulesError } = await query?.order('created_at', { ascending: false });
      
      if (rulesError) throw rulesError;
      
      // Fetch all countries separately
      const { data: allCountries, error: countriesError } = await supabase?.from('countries')?.select('id, country_name, country_code');
      
      if (countriesError) throw countriesError;
      
      // Map countries to rules based on country_ids array
      return rules?.map(rule => {
        const ruleCountries = (rule?.country_ids || [])?.map(countryId => 
          allCountries?.find(c => c?.id === countryId)
        )?.filter(Boolean);
        
        return {
   id: rule?.id,
  ruleName: rule?.rule_name,
  ruleCode: rule?.rule_code,
  ruleType: rule?.rule_type || rule?.discount_type, // ← CHANGED
  discountType: rule?.discount_type,
  description: rule?.description, // ← NEW
  discountPercentage: rule?.discount_percentage,
  minQuantity: rule?.min_quantity,
  minOrderQuantity: rule?.min_order_quantity,
  earlyPaymentDays: rule?.early_payment_days,
  minAmount: rule?.min_amount,
  countryIds: rule?.country_ids || [],
  countries: ruleCountries,
  validFrom: rule?.valid_from,
  validUntil: rule?.valid_until,
  isActive: rule?.is_active,
  priority: rule?.priority,
  conditions: rule?.conditions,
  createdAt: rule?.created_at,
  updatedAt: rule?.updated_at
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching discount rules:', error);
      throw error;
    }
  },

  // Create discount rule
createDiscountRule: async (ruleData) => {
  try {
    const insertData = {
      rule_name: ruleData?.ruleName,
      rule_type: ruleData?.ruleType,
      discount_type: ruleData?.ruleType,
      description: ruleData?.description || null,
      discount_percentage: ruleData?.discountPercentage,
      min_quantity: ruleData?.minQuantity || 0,
      min_order_quantity: ruleData?.minOrderQuantity || null,
      early_payment_days: ruleData?.earlyPaymentDays || null,
      min_amount: ruleData?.minAmount || 0,
      country_ids: ruleData?.countryIds || [],
      valid_from: ruleData?.validFrom || null,
      valid_until: ruleData?.validUntil || null,
      is_active: ruleData?.isActive !== undefined ? ruleData?.isActive : true,
      priority: ruleData?.priority || 0,
      conditions: ruleData?.conditions || {}
    };

    if (ruleData?.ruleCode) {
      insertData.rule_code = ruleData.ruleCode;
    }

    const { data, error } = await supabase?.from('discount_rules')?.insert(insertData)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      ruleName: data?.rule_name,
      ruleCode: data?.rule_code,
      ruleType: data?.rule_type || data?.discount_type,
      discountType: data?.discount_type,
      description: data?.description,
      discountPercentage: data?.discount_percentage,
      minQuantity: data?.min_quantity,
      minOrderQuantity: data?.min_order_quantity,
      earlyPaymentDays: data?.early_payment_days,
      minAmount: data?.min_amount,
      countryIds: data?.country_ids || [],
      validFrom: data?.valid_from,
      validUntil: data?.valid_until,
      isActive: data?.is_active,
      priority: data?.priority,
      conditions: data?.conditions,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  } catch (error) {
    console.error('Error creating discount rule:', error);
    throw error;
  }
},

  // Update discount rule
  updateDiscountRule: async (id, ruleData) => {
    try {
      const updateData = {};
      if (ruleData?.ruleName !== undefined) updateData.rule_name = ruleData?.ruleName;
if (ruleData?.ruleType !== undefined) {
  updateData.rule_type = ruleData?.ruleType;
  updateData.discount_type = ruleData?.ruleType;
}
if (ruleData?.description !== undefined) updateData.description = ruleData?.description;

if (ruleData?.discountType !== undefined) updateData.discount_type = ruleData?.discountType;
      if (ruleData?.ruleType !== undefined) updateData.rule_type = ruleData?.ruleType;
      if (ruleData?.discountPercentage !== undefined) updateData.discount_percentage = ruleData?.discountPercentage;
      if (ruleData?.minQuantity !== undefined) updateData.min_quantity = ruleData?.minQuantity;
      if (ruleData?.minOrderQuantity !== undefined) updateData.min_order_quantity = ruleData?.minOrderQuantity;
      if (ruleData?.earlyPaymentDays !== undefined) updateData.early_payment_days = ruleData?.earlyPaymentDays;
      if (ruleData?.minAmount !== undefined) updateData.min_amount = ruleData?.minAmount;
      if (ruleData?.countryIds !== undefined) updateData.country_ids = ruleData?.countryIds;
      if (ruleData?.validFrom !== undefined) updateData.valid_from = ruleData?.validFrom;
      if (ruleData?.validUntil !== undefined) updateData.valid_until = ruleData?.validUntil;
      if (ruleData?.isActive !== undefined) updateData.is_active = ruleData?.isActive;
      if (ruleData?.priority !== undefined) updateData.priority = ruleData?.priority;
      if (ruleData?.conditions !== undefined) updateData.conditions = ruleData?.conditions;
      
      const { data, error } = await supabase?.from('discount_rules')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
       id: data?.id,
  ruleName: data?.rule_name,
  ruleCode: data?.rule_code,
  ruleType: data?.rule_type || data?.discount_type, // ← CHANGED
  discountType: data?.discount_type,
  description: data?.description, // ← NEW
  discountPercentage: data?.discount_percentage,
  minQuantity: data?.min_quantity,
  minOrderQuantity: data?.min_order_quantity,
  earlyPaymentDays: data?.early_payment_days,
  minAmount: data?.min_amount,
  countryIds: data?.country_ids || [],
  validFrom: data?.valid_from,
  validUntil: data?.valid_until,
  isActive: data?.is_active,
  priority: data?.priority,
  conditions: data?.conditions,
  createdAt: data?.created_at,
  updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating discount rule:', error);
      throw error;
    }
  },

  // Delete discount rule
  deleteDiscountRule: async (id) => {
    try {
      const { error } = await supabase?.from('discount_rules')?.delete()?.eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting discount rule:', error);
      throw error;
    }
  },

  // Calculate automatic discount for invoice
  calculateAutomaticDiscount: async (invoiceAmount, quantity, countryId) => {
    try {
      const { data, error } = await supabase?.rpc('calculate_automatic_discount', {
        p_invoice_amount: invoiceAmount,
        p_quantity: quantity,
        p_country_id: countryId
      });
      
      if (error) throw error;
      
      if (data && data?.length > 0) {
        return {
          discountRuleId: data?.[0]?.discount_rule_id,
          discountPercentage: data?.[0]?.discount_percentage,
          discountAmount: data?.[0]?.discount_amount
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error calculating automatic discount:', error);
      throw error;
    }
  },

  // ============================================
  // PAYMENT PHASES MANAGEMENT
  // ============================================

  // Get payment phases for an invoice
  getPaymentPhases: async (invoiceId) => {
    try {
      const { data, error } = await supabase?.from('payment_phases')?.select('*')?.eq('invoice_id', invoiceId)?.order('phase_number', { ascending: true });
      
      if (error) throw error;
      
      return data?.map(phase => ({
        id: phase?.id,
        invoiceId: phase?.invoice_id,
        phaseNumber: phase?.phase_number,
        phaseName: phase?.phase_name,
        amount: phase?.amount,
        dueDate: phase?.due_date,
        status: phase?.status,
        paidDate: phase?.payment_date,
        paymentMethod: phase?.payment_method,
        notes: phase?.notes,
        createdAt: phase?.created_at,
        updatedAt: phase?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching payment phases:', error);
      throw error;
    }
  },

  // Create payment phase
  createPaymentPhase: async (phaseData) => {
    try {
      const { data, error } = await supabase?.from('payment_phases')?.insert({
        invoice_id: phaseData?.invoiceId,
        phase_number: phaseData?.phaseNumber,
        phase_name: phaseData?.phaseName,
        amount: phaseData?.amount,
        due_date: phaseData?.dueDate,
        status: phaseData?.status || 'pending',
        payment_date: phaseData?.paidDate || null,
        payment_method: phaseData?.paymentMethod || null,
        notes: phaseData?.notes || null
      })?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        invoiceId: data?.invoice_id,
        phaseNumber: data?.phase_number,
        phaseName: data?.phase_name,
        amount: data?.amount,
        dueDate: data?.due_date,
        status: data?.status,
        paidDate: data?.payment_date,
        paymentMethod: data?.payment_method,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating payment phase:', error);
      throw error;
    }
  },

  // Update payment phase
  updatePaymentPhase: async (id, phaseData) => {
    try {
      const updateData = {};
      if (phaseData?.phaseName !== undefined) updateData.phase_name = phaseData?.phaseName;
      if (phaseData?.amount !== undefined) updateData.amount = phaseData?.amount;
      if (phaseData?.dueDate !== undefined) updateData.due_date = phaseData?.dueDate;
      if (phaseData?.status !== undefined) updateData.status = phaseData?.status;
      if (phaseData?.paidDate !== undefined) updateData.payment_date = phaseData?.paidDate;
      if (phaseData?.paymentMethod !== undefined) updateData.payment_method = phaseData?.paymentMethod;
      if (phaseData?.notes !== undefined) updateData.notes = phaseData?.notes;
      
      const { data, error } = await supabase?.from('payment_phases')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        invoiceId: data?.invoice_id,
        phaseNumber: data?.phase_number,
        phaseName: data?.phase_name,
        amount: data?.amount,
        dueDate: data?.due_date,
        status: data?.status,
        paidDate: data?.payment_date,
        paymentMethod: data?.payment_method,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating payment phase:', error);
      throw error;
    }
  },

  // Delete payment phase
  deletePaymentPhase: async (id) => {
    try {
      const { error } = await supabase?.from('payment_phases')?.delete()?.eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting payment phase:', error);
      throw error;
    }
  },

  // ============================================
  // COUNTRIES MANAGEMENT
  // ============================================

  // Get all regions
  getAllRegions: async () => {
    try {
      console.log('Fetching all regions...');
      
      const { data, error } = await supabase?.from('regions')?.select('*')?.order('region_name', { ascending: true });
      
      console.log('Regions query result:', { data, error, rowCount: data?.length });
      
      if (error) {
        console.error('Supabase error fetching regions:', error);
        throw new Error(`Database error: ${error.message} (Code: ${error.code})`);
      }
      
      if (!data) {
        console.warn('No data returned from regions query');
        return [];
      }
      
      console.log(`Successfully fetched ${data?.length} regions`);
      
      const mappedData = data?.map(region => ({
        id: region?.id,
        regionCode: region?.region_code,
        regionName: region?.region_name,
        description: region?.description,
        isActive: region?.is_active,
        createdAt: region?.created_at,
        updatedAt: region?.updated_at
      }));
      
      console.log('Mapped regions data:', mappedData);
      
      return mappedData;
    } catch (error) {
      console.error('Error fetching regions:', error);
      throw error;
    }
  },

  // Create region
  createRegion: async (regionData) => {
    try {
      // Check if user has administrator role before attempting insert
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        throw new Error('You must be logged in to create regions');
      }

      // Check user's role
      const { data: roleCheck, error: roleError } = await supabase
        ?.from('user_role_assignments')
        ?.select(`
          id,
          is_active,
          user_roles!inner (
            role_type
          )
        `)
        ?.eq('user_id', user?.id)
        ?.eq('is_active', true)
        ?.eq('user_roles.role_type', 'administrator')
        ?.limit(1);

      if (roleError || !roleCheck || roleCheck?.length === 0) {
        throw new Error('You must have administrator privileges to create regions. Please contact your system administrator.');
      }

      const { data, error } = await supabase?.from('regions')?.insert({
        region_code: regionData?.regionCode,
        region_name: regionData?.regionName,
        description: regionData?.description || null,
        is_active: regionData?.isActive !== undefined ? regionData?.isActive : true
      })?.select()?.single();
      
      if (error) {
        // Provide user-friendly error message for RLS policy violation
        if (error?.code === '42501') {
          throw new Error('Access denied: Administrator privileges required to create regions');
        }
        throw error;
      }
      
      return {
        id: data?.id,
        regionCode: data?.region_code,
        regionName: data?.region_name,
        description: data?.description,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating region:', error);
      throw error;
    }
  },

  // Update region
  updateRegion: async (id, regionData) => {
    try {
      const updateData = {};
      if (regionData?.regionCode !== undefined) updateData.region_code = regionData?.regionCode;
      if (regionData?.regionName !== undefined) updateData.region_name = regionData?.regionName;
      if (regionData?.description !== undefined) updateData.description = regionData?.description;
      if (regionData?.isActive !== undefined) updateData.is_active = regionData?.isActive;
      updateData.updated_at = new Date()?.toISOString();
      
      const { data, error } = await supabase?.from('regions')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        regionCode: data?.region_code,
        regionName: data?.region_name,
        description: data?.description,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating region:', error);
      throw error;
    }
  },

  // Delete region
  deleteRegion: async (id) => {
    try {
      const { error } = await supabase?.from('regions')?.delete()?.eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting region:', error);
      throw error;
    }
  },

  // Get all languages
  getAllLanguages: async () => {
    try {
      const { data, error } = await supabase?.from('languages')?.select('*')?.order('language_name', { ascending: true });
      
      if (error) throw error;
      
      return data?.map(lang => ({
        id: lang?.id,
        languageCode: lang?.language_code,
        languageName: lang?.language_name,
        isActive: lang?.is_active,
        createdAt: lang?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching languages:', error);
      throw error;
    }
  },

  // Get all countries
  getAllCountries: async () => {
    try {
      const { data, error } = await supabase?.from('countries')?.select('*, region:regions(id, region_code, region_name)')?.order('country_name', { ascending: true });
      
      if (error) throw error;
      
      return data?.map(country => ({
        id: country?.id,
        countryCode: country?.country_code,
        countryName: country?.country_name,
        regionId: country?.region_id,
        region: country?.region,
        currencyCode: country?.currency_code,
        taxRate: country?.tax_rate,
        paymentMethods: country?.payment_methods,
        regulatoryRequirements: country?.regulatory_requirements,
        supportedLanguages: country?.supported_languages,
        isActive: country?.is_active,
        createdAt: country?.created_at,
        updatedAt: country?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching countries:', error);
      throw error;
    }
  },

  // Create country
  createCountry: async (countryData) => {
    try {
      const { data, error } = await supabase?.from('countries')?.insert({
        country_code: countryData?.countryCode,
        country_name: countryData?.countryName,
        region_id: countryData?.regionId,
        currency_code: countryData?.currencyCode,
        tax_rate: countryData?.taxRate || 0,
        payment_methods: countryData?.paymentMethods || [],
        regulatory_requirements: countryData?.regulatoryRequirements || {},
        supported_languages: countryData?.supportedLanguages || [],
        is_active: countryData?.isActive !== undefined ? countryData?.isActive : true
      })?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        countryCode: data?.country_code,
        countryName: data?.country_name,
        regionId: data?.region_id,
        currencyCode: data?.currency_code,
        taxRate: data?.tax_rate,
        paymentMethods: data?.payment_methods,
        regulatoryRequirements: data?.regulatory_requirements,
        supportedLanguages: data?.supported_languages,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating country:', error);
      throw error;
    }
  },

  // Update country
  updateCountry: async (id, countryData) => {
    try {
      const updateData = {};
      if (countryData?.countryCode !== undefined) updateData.country_code = countryData?.countryCode;
      if (countryData?.countryName !== undefined) updateData.country_name = countryData?.countryName;
      if (countryData?.regionId !== undefined) updateData.region_id = countryData?.regionId;
      if (countryData?.currencyCode !== undefined) updateData.currency_code = countryData?.currencyCode;
      if (countryData?.taxRate !== undefined) updateData.tax_rate = countryData?.taxRate;
      if (countryData?.paymentMethods !== undefined) updateData.payment_methods = countryData?.paymentMethods;
      if (countryData?.regulatoryRequirements !== undefined) updateData.regulatory_requirements = countryData?.regulatoryRequirements;
      if (countryData?.supportedLanguages !== undefined) updateData.supported_languages = countryData?.supportedLanguages;
      if (countryData?.isActive !== undefined) updateData.is_active = countryData?.isActive;
      
      const { data, error } = await supabase?.from('countries')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        countryCode: data?.country_code,
        countryName: data?.country_name,
        regionId: data?.region_id,
        currencyCode: data?.currency_code,
        taxRate: data?.tax_rate,
        paymentMethods: data?.payment_methods,
        regulatoryRequirements: data?.regulatory_requirements,
        supportedLanguages: data?.supported_languages,
        isActive: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating country:', error);
      throw error;
    }
  },

  // Delete country
  deleteCountry: async (id) => {
    try {
      const { error } = await supabase?.from('countries')?.delete()?.eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting country:', error);
      throw error;
    }
  },

  // ============================================
  // APPLIED DISCOUNTS
  // ============================================

  // Get applied discounts for an invoice
  getAppliedDiscounts: async (invoiceId) => {
    try {
      const { data, error } = await supabase?.from('applied_discounts')?.select('*, discount_rule:discount_rules(*)')?.eq('invoice_id', invoiceId);
      
      if (error) throw error;
      
      return data?.map(discount => ({
        id: discount?.id,
        invoiceId: discount?.invoice_id,
        discountRuleId: discount?.discount_rule_id,
        discountRule: discount?.discount_rule,
        discountAmount: discount?.discount_amount,
        appliedAt: discount?.applied_at
      })) || [];
    } catch (error) {
      console.error('Error fetching applied discounts:', error);
      throw error;
    }
  },

  // Apply discount to invoice
  applyDiscount: async (invoiceId, discountRuleId, discountAmount) => {
    try {
      const { data, error } = await supabase?.from('applied_discounts')?.insert({
        invoice_id: invoiceId,
        discount_rule_id: discountRuleId,
        discount_amount: discountAmount
      })?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        invoiceId: data?.invoice_id,
        discountRuleId: data?.discount_rule_id,
        discountAmount: data?.discount_amount,
        appliedAt: data?.applied_at
      };
    } catch (error) {
      console.error('Error applying discount:', error);
      throw error;
    }
  },

  // ============================================
  // MILESTONE TRACKING
  // ============================================

  // Get all milestones for a payment phase
  getMilestones: async (paymentPhaseId) => {
    try {
      const { data, error } = await supabase
        ?.from('milestone_tracking')
        ?.select('*, assigned_user:assigned_to(id, email), payment_phase:payment_phases(phase_name, phase_number)')
        ?.eq('payment_phase_id', paymentPhaseId)
        ?.order('created_at', { ascending: true });
      
      if (error) throw error;
      
      // Fetch user profiles separately to avoid foreign key issues
      const userIds = data?.filter(m => m?.assigned_to)?.map(m => m?.assigned_to) || [];
      let userProfiles = [];
      
      if (userIds?.length > 0) {
        const { data: profiles } = await supabase
          ?.from('user_profiles')
          ?.select('id, full_name, email')
          ?.in('id', userIds);
        userProfiles = profiles || [];
      }
      
      return data?.map(milestone => {
        const assignedUser = userProfiles?.find(u => u?.id === milestone?.assigned_to);
        return {
          id: milestone?.id,
          paymentPhaseId: milestone?.payment_phase_id,
          milestoneName: milestone?.milestone_name,
          milestoneDescription: milestone?.milestone_description,
          targetDate: milestone?.target_date,
          completionDate: milestone?.completion_date,
          status: milestone?.status,
          completionPercentage: milestone?.completion_percentage,
          assignedTo: milestone?.assigned_to,
          assignedUser: assignedUser ? {
            id: assignedUser?.id,
            fullName: assignedUser?.full_name,
            email: assignedUser?.email
          } : null,
          dependencies: milestone?.dependencies,
          metadata: milestone?.metadata,
          createdAt: milestone?.created_at,
          updatedAt: milestone?.updated_at
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching milestones:', error);
      throw error;
    }
  },

  // Get milestone summary for all phases of an invoice
  getMilestoneSummaryByInvoice: async (invoiceId) => {
    try {
      // First get all payment phases for the invoice
      const { data: phases, error: phasesError } = await supabase
        ?.from('payment_phases')
        ?.select('id, phase_number, phase_name, milestone_status, milestone_completion_percentage')
        ?.eq('invoice_id', invoiceId)
        ?.order('phase_number', { ascending: true });
      
      if (phasesError) throw phasesError;
      
      // Get milestones for all phases
      const phaseIds = phases?.map(p => p?.id) || [];
      const { data: milestones, error: milestonesError } = await supabase
        ?.from('milestone_tracking')
        ?.select('*')
        ?.in('payment_phase_id', phaseIds);
      
      if (milestonesError) throw milestonesError;
      
      // Group milestones by phase
      return phases?.map(phase => {
        const phaseMilestones = milestones?.filter(m => m?.payment_phase_id === phase?.id) || [];
        return {
          phaseId: phase?.id,
          phaseNumber: phase?.phase_number,
          phaseName: phase?.phase_name,
          milestoneStatus: phase?.milestone_status,
          completionPercentage: phase?.milestone_completion_percentage,
          totalMilestones: phaseMilestones?.length,
          completedMilestones: phaseMilestones?.filter(m => m?.status === 'completed')?.length,
          inProgressMilestones: phaseMilestones?.filter(m => m?.status === 'in_progress')?.length,
          delayedMilestones: phaseMilestones?.filter(m => m?.status === 'delayed')?.length
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching milestone summary:', error);
      throw error;
    }
  },

  // Create milestone
  createMilestone: async (milestoneData) => {
    try {
      const { data, error } = await supabase?.from('milestone_tracking')?.insert({
        payment_phase_id: milestoneData?.paymentPhaseId,
        milestone_name: milestoneData?.milestoneName,
        milestone_description: milestoneData?.milestoneDescription || null,
        target_date: milestoneData?.targetDate || null,
        completion_date: milestoneData?.completionDate || null,
        status: milestoneData?.status || 'not_started',
        completion_percentage: milestoneData?.completionPercentage || 0,
        assigned_to: milestoneData?.assignedTo || null,
        dependencies: milestoneData?.dependencies || [],
        metadata: milestoneData?.metadata || {}
      })?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        paymentPhaseId: data?.payment_phase_id,
        milestoneName: data?.milestone_name,
        milestoneDescription: data?.milestone_description,
        targetDate: data?.target_date,
        completionDate: data?.completion_date,
        status: data?.status,
        completionPercentage: data?.completion_percentage,
        assignedTo: data?.assigned_to,
        dependencies: data?.dependencies,
        metadata: data?.metadata,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating milestone:', error);
      throw error;
    }
  },

  // Update milestone
  updateMilestone: async (id, milestoneData) => {
    try {
      const updateData = {};
      if (milestoneData?.milestoneName !== undefined) updateData.milestone_name = milestoneData?.milestoneName;
      if (milestoneData?.milestoneDescription !== undefined) updateData.milestone_description = milestoneData?.milestoneDescription;
      if (milestoneData?.targetDate !== undefined) updateData.target_date = milestoneData?.targetDate;
      if (milestoneData?.completionDate !== undefined) updateData.completion_date = milestoneData?.completionDate;
      if (milestoneData?.status !== undefined) updateData.status = milestoneData?.status;
      if (milestoneData?.completionPercentage !== undefined) updateData.completion_percentage = milestoneData?.completionPercentage;
      if (milestoneData?.assignedTo !== undefined) updateData.assigned_to = milestoneData?.assignedTo;
      if (milestoneData?.dependencies !== undefined) updateData.dependencies = milestoneData?.dependencies;
      if (milestoneData?.metadata !== undefined) updateData.metadata = milestoneData?.metadata;
      
      const { data, error } = await supabase?.from('milestone_tracking')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        paymentPhaseId: data?.payment_phase_id,
        milestoneName: data?.milestone_name,
        milestoneDescription: data?.milestone_description,
        targetDate: data?.target_date,
        completionDate: data?.completion_date,
        status: data?.status,
        completionPercentage: data?.completion_percentage,
        assignedTo: data?.assigned_to,
        dependencies: data?.dependencies,
        metadata: data?.metadata,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating milestone:', error);
      throw error;
    }
  },

  // Delete milestone
  deleteMilestone: async (id) => {
    try {
      const { error } = await supabase?.from('milestone_tracking')?.delete()?.eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting milestone:', error);
      throw error;
    }
  },

  // ============================================
  // COLLECTION TEAM ASSIGNMENTS
  // ============================================

  // Get collection team for a payment phase
  getCollectionTeam: async (paymentPhaseId) => {
    try {
      const { data, error } = await supabase
        ?.from('collection_team_assignments')
        ?.select('*')
        ?.eq('payment_phase_id', paymentPhaseId)
        ?.eq('is_active', true)
        ?.order('assigned_date', { ascending: true });
      
      if (error) throw error;
      
      // Fetch user profiles separately
      const userIds = data?.map(a => a?.user_id) || [];
      let userProfiles = [];
      
      if (userIds?.length > 0) {
        const { data: profiles } = await supabase
          ?.from('user_profiles')
          ?.select('id, full_name, email, phone')
          ?.in('id', userIds);
        userProfiles = profiles || [];
      }
      
      return data?.map(assignment => {
        const user = userProfiles?.find(u => u?.id === assignment?.user_id);
        return {
          id: assignment?.id,
          paymentPhaseId: assignment?.payment_phase_id,
          userId: assignment?.user_id,
          user: user ? {
            id: user?.id,
            fullName: user?.full_name,
            email: user?.email,
            phone: user?.phone
          } : null,
          collectionRole: assignment?.collection_role,
          assignedDate: assignment?.assigned_date,
          isActive: assignment?.is_active,
          notes: assignment?.notes,
          createdAt: assignment?.created_at,
          updatedAt: assignment?.updated_at
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching collection team:', error);
      throw error;
    }
  },

  // Get collection team summary for all phases of an invoice
  getCollectionTeamByInvoice: async (invoiceId) => {
    try {
      // First get all payment phases for the invoice
      const { data: phases, error: phasesError } = await supabase
        ?.from('payment_phases')
        ?.select('id, phase_number, phase_name, status')
        ?.eq('invoice_id', invoiceId)
        ?.order('phase_number', { ascending: true });
      
      if (phasesError) throw phasesError;
      
      // Get collection team for all phases
      const phaseIds = phases?.map(p => p?.id) || [];
      const { data: assignments, error: assignmentsError } = await supabase
        ?.from('collection_team_assignments')
        ?.select('*')
        ?.in('payment_phase_id', phaseIds)
        ?.eq('is_active', true);
      
      if (assignmentsError) throw assignmentsError;
      
      // Fetch all user profiles
      const userIds = [...new Set(assignments?.map(a => a?.user_id) || [])];
      let userProfiles = [];
      
      if (userIds?.length > 0) {
        const { data: profiles } = await supabase
          ?.from('user_profiles')
          ?.select('id, full_name, email')
          ?.in('id', userIds);
        userProfiles = profiles || [];
      }
      
      // Group assignments by phase
      return phases?.map(phase => {
        const phaseAssignments = assignments?.filter(a => a?.payment_phase_id === phase?.id) || [];
        return {
          phaseId: phase?.id,
          phaseNumber: phase?.phase_number,
          phaseName: phase?.phase_name,
          phaseStatus: phase?.status,
          teamMembers: phaseAssignments?.map(assignment => {
            const user = userProfiles?.find(u => u?.id === assignment?.user_id);
            return {
              id: assignment?.id,
              userId: assignment?.user_id,
              userName: user?.full_name || user?.email || 'Unknown',
              collectionRole: assignment?.collection_role,
              assignedDate: assignment?.assigned_date
            };
          })
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching collection team by invoice:', error);
      throw error;
    }
  },

  // Assign user to collection team
  assignCollectionTeam: async (assignmentData) => {
    try {
      const { data, error } = await supabase?.from('collection_team_assignments')?.insert({
        payment_phase_id: assignmentData?.paymentPhaseId,
        user_id: assignmentData?.userId,
        collection_role: assignmentData?.collectionRole,
        is_active: assignmentData?.isActive !== undefined ? assignmentData?.isActive : true,
        notes: assignmentData?.notes || null
      })?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        paymentPhaseId: data?.payment_phase_id,
        userId: data?.user_id,
        collectionRole: data?.collection_role,
        assignedDate: data?.assigned_date,
        isActive: data?.is_active,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error assigning collection team:', error);
      throw error;
    }
  },

  // Update collection team assignment
  updateCollectionTeamAssignment: async (id, assignmentData) => {
    try {
      const updateData = {};
      if (assignmentData?.collectionRole !== undefined) updateData.collection_role = assignmentData?.collectionRole;
      if (assignmentData?.isActive !== undefined) updateData.is_active = assignmentData?.isActive;
      if (assignmentData?.notes !== undefined) updateData.notes = assignmentData?.notes;
      
      const { data, error } = await supabase?.from('collection_team_assignments')?.update(updateData)?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        paymentPhaseId: data?.payment_phase_id,
        userId: data?.user_id,
        collectionRole: data?.collection_role,
        assignedDate: data?.assigned_date,
        isActive: data?.is_active,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating collection team assignment:', error);
      throw error;
    }
  },

  // Remove user from collection team
  removeCollectionTeamMember: async (id) => {
    try {
      const { error } = await supabase?.from('collection_team_assignments')?.delete()?.eq('id', id);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error removing collection team member:', error);
      throw error;
    }
  },

  // ============================================
  // PHASE PROGRESSION LOGS
  // ============================================

  // Get phase progression logs
  getPhaseProgressionLogs: async (paymentPhaseId) => {
    try {
      const { data, error } = await supabase
        ?.from('phase_progression_logs')
        ?.select('*')
        ?.eq('payment_phase_id', paymentPhaseId)
        ?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Fetch user profiles for triggered_by
      const userIds = data?.filter(log => log?.triggered_by)?.map(log => log?.triggered_by) || [];
      let userProfiles = [];
      
      if (userIds?.length > 0) {
        const { data: profiles } = await supabase
          ?.from('user_profiles')
          ?.select('id, full_name, email')
          ?.in('id', userIds);
        userProfiles = profiles || [];
      }
      
      return data?.map(log => {
        const triggeredByUser = userProfiles?.find(u => u?.id === log?.triggered_by);
        return {
          id: log?.id,
          paymentPhaseId: log?.payment_phase_id,
          fromStatus: log?.from_status,
          toStatus: log?.to_status,
          triggerType: log?.trigger_type,
          triggeredBy: log?.triggered_by,
          triggeredByUser: triggeredByUser ? {
            id: triggeredByUser?.id,
            fullName: triggeredByUser?.full_name,
            email: triggeredByUser?.email
          } : null,
          triggerDetails: log?.trigger_details,
          automated: log?.automated,
          createdAt: log?.created_at
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching phase progression logs:', error);
      throw error;
    }
  },

  // Get progression logs for all phases of an invoice
  getProgressionLogsByInvoice: async (invoiceId) => {
    try {
      // First get all payment phases for the invoice
      const { data: phases, error: phasesError } = await supabase
        ?.from('payment_phases')
        ?.select('id, phase_number, phase_name')
        ?.eq('invoice_id', invoiceId)
        ?.order('phase_number', { ascending: true });
      
      if (phasesError) throw phasesError;
      
      // Get logs for all phases
      const phaseIds = phases?.map(p => p?.id) || [];
      const { data: logs, error: logsError } = await supabase
        ?.from('phase_progression_logs')
        ?.select('*')
        ?.in('payment_phase_id', phaseIds)
        ?.order('created_at', { ascending: false });
      
      if (logsError) throw logsError;
      
      return logs?.map(log => {
        const phase = phases?.find(p => p?.id === log?.payment_phase_id);
        return {
          id: log?.id,
          paymentPhaseId: log?.payment_phase_id,
          phaseName: phase?.phase_name,
          phaseNumber: phase?.phase_number,
          fromStatus: log?.from_status,
          toStatus: log?.to_status,
          triggerType: log?.trigger_type,
          automated: log?.automated,
          triggerDetails: log?.trigger_details,
          createdAt: log?.created_at
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching progression logs by invoice:', error);
      throw error;
    }
  }
};

export default advancedBillingService;